import { useRef, useEffect } from 'react'
import * as R from 'ramda'

import { guid } from 'src/lib/utils'
import { socketSend } from 'src/lib/socket'
import { useOnlineUserContext } from 'src/lib/online-user-context'
import { USER_ONLINE_QUERY } from 'src/graphql/user/online-user'
import { useLazyQuery } from '@apollo/client'

import { isErr, isLoading } from './useApolloQuery'
import { useLoggedInUser } from './use-logged-in-user'
import config from 'config'

const subscribedComponentsMap: Record<string, string[]> = {}

export const useIsUserOnline = (userId: string) => {
  const { onlineUsers, setOnlineUsers } = useOnlineUserContext()
  const loggedInUser = useLoggedInUser()

  const currentUserId =
    isLoading(loggedInUser) || isErr(loggedInUser)
      ? null
      : loggedInUser.data.loggedInUser.id

  const componentId = useRef(guid())
  const isUserOnline = onlineUsers[userId]

  const [getIsUserOnline] = useLazyQuery(USER_ONLINE_QUERY, {
    variables: { userId },
    onCompleted: userOnlineData => {
      if (userOnlineData.user?.isOnline) {
        setOnlineUsers(userId, true)
      }
    }
  })

  // Join room and manage subscriptions
  // Socket event listeners (userOnline/userOffline) are handled globally in OnlineUserContextProvider
  // Only run when we have currentUserId so we never send join with userId: null.
  // Do not depend on onlineUsers - it changes when context updates and would cause leave/join cycles.
  useEffect(() => {
    if (!currentUserId) return

    const isFirstSubscriber = !subscribedComponentsMap[userId]

    if (isFirstSubscriber) {
      subscribedComponentsMap[userId] = []

      socketSend({
        action: 'join',
        data: { roomId: userId, userId: currentUserId }
      })

      if (!onlineUsers[userId] && config.NEXT_PUBLIC_SOCKET_SERVER_URL) {
        getIsUserOnline()
      }
    }

    subscribedComponentsMap[userId].push(componentId.current)

    const capturedUserId = userId

    return () => {
      if (!subscribedComponentsMap[capturedUserId]) return

      subscribedComponentsMap[capturedUserId] = R.without(
        [componentId.current],
        subscribedComponentsMap[capturedUserId]
      )

      if (R.isEmpty(subscribedComponentsMap[capturedUserId])) {
        socketSend({ action: 'leave', data: { roomIds: [capturedUserId] } })
        delete subscribedComponentsMap[capturedUserId]
      }
    }
  }, [userId, currentUserId, setOnlineUsers])

  return isUserOnline || currentUserId === userId
}
